import type { components } from "./schema";
import {
  BRANCH_SCOPE,
  scopeHeaders,
  type BrandingAsset,
  type BrandingUpdate,
  type ListScope,
} from "@/lib/api/distributors";
import { apiClient } from "./client";
import { organizationsChanged } from "./orgEvents";
import { fetchAllPages } from "./paginate";
import type { OwnershipFields } from "./ownership";

/** The organization lifecycle states the API reports. */
export type MSPStatus = components["schemas"]["OrgStatusEnum"];

export interface MSP extends OwnershipFields {
  id: string;
  name: string;
  slug: string;
  /** FK to Distributor; API may return string or number */
  distributor: string | number | null;
  /** Display name of managing distributor (null if unassigned / legacy row). */
  distributor_name?: string | null;
  /** Lifecycle state; drives the approve/reject moderation actions. */
  status?: MSPStatus;
  /** Attached companies; a MSP with companies cannot be deleted. */
  company_count?: number;
  user_count?: number;
  contact_email?: string | null;
  is_active: boolean;
  /** Registration token a company uses to register under this MSP. Read-only; rotate via rotateToken. */
  token?: string;
  /**
   * White-label branding for this MSP's branch. The nearest branded
   * organization wins, so an MSP's branding beats its distributor's for
   * everyone beneath the MSP.
   */
  enable_branding?: boolean;
  logo?: string | null;
  logo_dark?: string | null;
  favicon?: string | null;
  primary_color?: string;
  accent_color?: string;
  brand_colors?: Record<string, string>;
  product_name?: string;
}

export interface MSPCreateInput {
  name: string;
  slug: string;
  contact_email?: string;
  is_active?: boolean;
  /** The distributor it sits under; omit to let the server use the caller's. */
  distributor?: string | null;
}

export interface MSPUpdateInput {
  name?: string;
  slug?: string;
  contact_email?: string;
  /**
   * Switch the MSP off (false) or back on (true). Moves with `status` on the
   * server, and contradicting it is refused.
   */
  is_active?: boolean;
  status?: Extract<MSPStatus, "active" | "suspended">;
  distributor?: string | number | null;
  /** Assign the responsible user (owner). User UUID, not a number. */
  owner?: string | null;
}

export interface OnboardPayload {
  distributor_token: string;
  name: string;
  slug: string;
  contact_email: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirm: string;
}

export interface OnboardResponse {
  access: string;
  refresh: string;
  msp_status: string;
}

export const mspsApi = {
  /**
   * Every MSP in scope, following the server's pages to the end.
   *
   * Takes no `page_size`. The old one was misleading twice over: the server
   * capped it at 100 whatever was asked, and the screens that passed nothing at
   * all rendered the first 25 as though that were the whole list.
   */
  list: async (opts?: { scope?: ListScope }): Promise<MSP[]> => {
    return fetchAllPages<MSP>("/msps/", scopeHeaders(opts?.scope));
  },
  /** Issue a new company-registration token, invalidating the previous one. */
  rotateToken: async (
    mspId: string,
    opts?: { scope?: ListScope },
  ): Promise<{ token: string }> => {
    const response = await apiClient.post(`/msps/${mspId}/rotate-token/`, undefined, {
      headers: scopeHeaders(opts?.scope),
    });
    return response.data;
  },
  /**
   * One MSP. Pass `{ scope: "branch" }` when it is the caller's own: with the
   * scope picker on one of its companies the MSP is outside that scope, and the
   * plain request answers 404.
   */
  retrieve: async (mspId: string, opts?: { scope?: ListScope }): Promise<MSP> => {
    const response = await apiClient.get(`/msps/${mspId}/`, {
      headers: scopeHeaders(opts?.scope),
    });
    return response.data;
  },
  create: async (data: MSPCreateInput): Promise<MSP> => {
    const response = await apiClient.post("/msps/", data);
    organizationsChanged();
    return response.data;
  },
  /**
   * Pass the `scope` the MSP was listed with, so the scope picker cannot hide
   * the row being changed.
   */
  update: async (
    mspId: string,
    data: MSPUpdateInput,
    opts?: { scope?: ListScope },
  ): Promise<MSP> => {
    const response = await apiClient.patch(`/msps/${mspId}/`, data, {
      headers: scopeHeaders(opts?.scope),
    });
    organizationsChanged();
    return response.data;
  },
  destroy: async (mspId: string): Promise<void> => {
    await apiClient.delete(`/msps/${mspId}/`);
    organizationsChanged();
  },
  /** Update branding (colors map / product name / toggle). MSP admins patch their own row. */
  updateBranding: async (mspId: string, data: BrandingUpdate): Promise<MSP> => {
    const response = await apiClient.patch(`/msps/${mspId}/`, data, {
      headers: { ...BRANCH_SCOPE },
    });
    return response.data;
  },
  /** Upload a branding asset (logo, dark logo, or favicon) via multipart. */
  uploadAsset: async (
    mspId: string,
    field: BrandingAsset,
    file: File,
  ): Promise<MSP> => {
    const form = new FormData();
    form.append(field, file);
    const response = await apiClient.patch(`/msps/${mspId}/`, form, {
      headers: { ...BRANCH_SCOPE, "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  onboard: async (data: OnboardPayload): Promise<OnboardResponse> => {
    const response = await apiClient.post("/msps/onboard/", data);
    organizationsChanged();
    return response.data;
  },
  approve: async (mspId: string): Promise<{ status: string }> => {
    const response = await apiClient.post(`/msps/${mspId}/approve/`);
    organizationsChanged();
    return response.data;
  },
  reject: async (mspId: string): Promise<{ status: string }> => {
    const response = await apiClient.post(`/msps/${mspId}/reject/`);
    organizationsChanged();
    return response.data;
  },
};
